import { AgentCampaignDetailsModel, AgentCampaignHotelModel } from '@/__generated';
import CampaignImage from '@/components/App/CampaignImage';
import DefaultHeader from '@/components/App/Header/DefaultHeader';
import HotelJapaneseName from '@/components/App/Hotels/HotelJapaneseName';
import HotelStar from '@/components/App/Hotels/HotelStar';
import PageLoading from '@/components/App/PageLoading';
import { Swiper, SwiperSlide } from 'swiper/react';
import { appDisplayDateFormat, formatDateOnly, HOTEL_CONFIG, HOTEL_SEARCHBOX_CONFIG } from '@/config';
import history from '@/history';
import { apiSdk } from '@/httpClient';
import { AppUrls } from '@/urls';
import moment from 'moment';
import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { Autoplay, Navigation, Pagination } from 'swiper';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import { convert } from 'url-slug';
import { useGetSiteSettings } from '@/contexts/SiteSettingContext';

const nextId = 'Campaign_NextId';
const prevId = 'Campaign_PrevId';
const pagingId = 'Campaign_PagingId';

const createHotelCampaignUrl = (hotel: AgentCampaignHotelModel, campaign: AgentCampaignDetailsModel, cutoffDays: number) => {
  let url = `/${convert(hotel.hotelName)}/hotel/${hotel.omegaHotelCode}`;
  let checkInDate = moment(HOTEL_SEARCHBOX_CONFIG.__defaultStartDate(cutoffDays));
  if (checkInDate.isBefore(moment(campaign.dateFrom))) {
    checkInDate = moment(campaign.dateFrom);
  }
  url += `?checkinDate=${formatDateOnly(checkInDate)}`;
  url += `&checkoutDate=${formatDateOnly(checkInDate.clone().add(campaign.noOfNights, 'day'))}`;
  let roomValue = [2, 0, ''].join('-');
  url += `&rooms=${roomValue}`;
  url += `&boardType=${campaign.boardType}`;
  url += `&campaignRatePlan=${hotel.ratePlanCodes}`;

  return url;
};

export default function CampaignsPage() {
  const { cutoffDays = 0 } = useGetSiteSettings();
  const { slug } = useParams<{ slug: string }>();
  const [loading, setIsLoading] = React.useState(true);
  const [campaign, setCampaign] = React.useState<AgentCampaignDetailsModel>();
  const { formatCurrency } = useCurrencyFormat();
  const [headlineBgImage, setHeadlineBgImage] = React.useState<string>();

  const [hotels, setHotels] = React.useState<AgentCampaignHotelModel[]>([]);

  React.useEffect(() => {
    setIsLoading(true);
    apiSdk.CampaignService.getCampaignHotels({ slug })
      .then(res => {
        setHotels(res);
      })
      .catch(() => {
        setHotels([]);
      });
    apiSdk.CampaignService.getDetails({ slug })
      .then(res => {
        setCampaign(res);
      })
      .catch(err => {
        history.push(AppUrls.HomePage);
      })
      .finally(() => setIsLoading(false));
  }, [slug]);
  React.useEffect(() => {
    if (campaign?.headlinePhotoId) {
      apiSdk.AllowCacheService.downloadCampaignImage(
        { fileId: campaign?.headlinePhotoId, campaignId: campaign.id || '' },
        { responseType: 'blob' },
      ).then(res => {
        setHeadlineBgImage(window.URL.createObjectURL(new Blob([res])));
      });
    }
  }, [campaign?.headlinePhotoId]);

  const imageClass = React.useMemo(() => {
    if (campaign?.subPhotosIds?.length === 2) {
      return 'col-sm-12 col-md-12 col-lg-6 col-xl-6';
    }
    if (campaign?.subPhotosIds?.length === 3) {
      return 'col-sm-12 col-md-12 col-lg-4 col-xl-4';
    }
    return 'col-sm-12 col-md-12 col-lg-12 col-xl-12';
  }, [campaign]);

  const { title, description } = React.useMemo(() => {
    if (!campaign) {
      return {
        title: 'NTA - Omega',
        description: '',
      };
    }
    let titleDiv = document.createElement('div');
    let descriptionDiv = document.createElement('div');
    titleDiv.innerHTML = [campaign.caption, campaign.headline].filter(x => x).join(', ');
    descriptionDiv.innerHTML = campaign.freeText || '';
    return {
      title: titleDiv.innerText,
      description: descriptionDiv.innerText,
    };
  }, [campaign]);

  if (loading) return <PageLoading />;

  return campaign ? (
    //
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description || ''} />
      </Helmet>
      <DefaultHeader isSticky />
      <div className="header-margin" />

      <div className="text-16 ql-editor">
        <div
          className="mx-auto"
          style={
            headlineBgImage
              ? {
                  backgroundImage: `url(${headlineBgImage})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : {}
          }
        >
          <div className="pt-30 pb-30" style={{ backgroundColor: '#ffffffb3' }}>
            <div className="container p-5  gap-2">
              {campaign.caption ? <div dangerouslySetInnerHTML={{ __html: campaign.caption }}></div> : null}
              {campaign.headline ? <div dangerouslySetInnerHTML={{ __html: campaign.headline }}></div> : null}
              <div className="text-center">{`${appDisplayDateFormat.default.dateOnly(campaign.dateFrom)} ~ ${appDisplayDateFormat.default.dateOnly(
                campaign.dateTo,
              )}`}</div>
              {campaign.freeText ? <div dangerouslySetInnerHTML={{ __html: campaign.freeText }}></div> : null}
            </div>
          </div>
        </div>
        <div className="container mt-50">
          {campaign.desc1 ? <div dangerouslySetInnerHTML={{ __html: campaign.desc1 || '' }}></div> : null}{' '}
          <div className="row">
            <div className="col-sm-12 col-md-12 col-lg-6 col-xl-8">
              {campaign.desc2 ? <div dangerouslySetInnerHTML={{ __html: campaign.desc2 || '' }}></div> : null}
            </div>
            <div className="col-sm-12 col-md-12 col-lg-6 col-xl-4">
              <CampaignImage campaignId={campaign.id || ''} className="w-full" style={{ borderRadius: 10 }} imageId={campaign.topPhotoId || ''} />
            </div>
          </div>
        </div>
        <div className="container mt-50 ">
          <div className="row row-gap-2">
            {campaign.subPhotosIds?.map(photoId => (
              <div className={imageClass} key={photoId}>
                <CampaignImage campaignId={campaign.id || ''} className="w-full" style={{ borderRadius: 10 }} imageId={photoId || ''} />
              </div>
            ))}
          </div>
        </div>
        <section className="layout-pt-sm layout-pb-sm">
          <div className="container">
            <div className="row y-gap-10 justify-between items-end">
              <div className="col-auto">
                <div className="sectionTitle -md">
                  <h2 className="sectionTitle__title">Campaign hotels</h2>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden pt-40 sm:pt-20 js-section-slider item_gap-x30">
              <Swiper
                spaceBetween={20}
                modules={[Navigation, Pagination, Autoplay]}
                autoplay
                navigation={{
                  nextEl: `#${nextId}`,
                  prevEl: `#${prevId}`,
                }}
                pagination={{
                  el: `#${pagingId}`,
                  clickable: true,
                }}
                breakpoints={{
                  540: {
                    slidesPerView: 2,
                  },
                  768: {
                    slidesPerView: 3,
                  },
                  1024: {
                    slidesPerView: 4,
                  },
                  1200: {
                    slidesPerView: 5,
                  },
                  1400: {
                    slidesPerView: 6,
                  },
                }}
              >
                {hotels?.map(hotel => (
                  <SwiperSlide key={hotel.hotelCode}>
                    <a href={createHotelCampaignUrl(hotel, campaign, cutoffDays)} target="_blank" className="hotelsCard -type-1 hover-inside-slider">
                      <div className="hotelsCard__image">
                        <div className="cardImage ratio ratio-1:1">
                          <div className="cardImage__content">
                            <div className="cardImage-slider rounded-4 overflow-hidden custom_inside-slider">
                              <Swiper className="mySwiper" modules={[Navigation]} pagination={false} navigation={true}>
                                {hotel?.hotelImages?.length ? (
                                  hotel?.hotelImages?.map((slide, i) => (
                                    <SwiperSlide key={i}>
                                      <img className="w-100 aspect-ratio-1-1 col-12 rounded-4" src={slide} alt="hotel" />
                                    </SwiperSlide>
                                  ))
                                ) : (
                                  <SwiperSlide>
                                    <img className="w-100 aspect-ratio-1-1 col-12 rounded-4" src={HOTEL_CONFIG.defaultImage} alt="hotel" />
                                  </SwiperSlide>
                                )}
                              </Swiper>
                            </div>
                          </div>
                        </div>

                        <div className="cardImage__leftBadge"></div>
                      </div>
                      <div className="hotelsCard__content mt-10">
                        <h4 className="hotelsCard__title text-dark-1 text-18 lh-16 fw-500">
                          <span>{hotel?.hotelName}</span>
                          <HotelJapaneseName name={hotel.originalName} addBefore="-" />
                        </h4>

                        <p className="text-light-1 lh-14 text-14 mt-5">{hotel.address}</p>
                        {hotel.starRating ? (
                          <div className="d-flex items-center">
                            <HotelStar star={hotel.starRating} />
                          </div>
                        ) : null}

                        <div className="mt-5">
                          <div className="fw-500">
                            Starting from <span className="text-blue-1">{formatCurrency(hotel.cheapestPrice)}</span>
                          </div>
                        </div>
                      </div>
                    </a>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className="d-flex x-gap-15 items-center justify-center pt-40 sm:pt-20">
                <div className="col-auto">
                  <button className="d-flex items-center text-24 arrow-left-hover" id={prevId}>
                    <i className="icon icon-arrow-left" />
                  </button>
                </div>
                {/* End .prev */}

                <div className="col-auto">
                  <div className="pagination -dots text-border" id={pagingId} />
                </div>
                {/* End .pagination */}

                <div className="col-auto">
                  <button className="d-flex items-center text-24 arrow-right-hover" id={nextId}>
                    <i className="icon icon-arrow-right" />
                  </button>
                </div>
                {/* End .next */}
              </div>
            </div>
          </div>
        </section>
        <div className="bg-blue-2 pb-50 pt-50 text-16">
          <div className="container  text-center ">
            <div>
              <div>This campaign is exclusively for bookings made through our online booking system, New Omega.</div>
              <div>Please note that NTA cannot book rooms via email.</div>
              <div>If you have any questions, feel free to reach out to us at omega_support@nta.co.jp.</div>
            </div>
          </div>
        </div>
      </div>
    </>
  ) : null;
}
